package dev.lingvo.shared.screenshots.doc

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * A colour as a document writes it: `#RRGGBB`, `#RRGGBBAA` (uppercase hex), or a palette token
 * `@bg|@surface|@ink|@inkMuted|@accent|@accentInk|@slot` with an optional `/alpha` in 0..1 (`@accent/0.4`).
 *
 * Templates write tokens, never hex, so changing the brand recolours the whole set. `@slot` is
 * `palette.slotAccents[slotIndex % size]`, falling back to `@accent`.
 */
typealias ColorRef = String

@Serializable
sealed interface Paint {
    @Serializable
    @SerialName("solid")
    data class Solid(val color: ColorRef) : Paint

    /** [angle] in degrees, CSS convention (0 = up, 90 = right), the line through the box centre as CSS sizes it. */
    @Serializable
    @SerialName("linear")
    data class Linear(val angle: Double, val stops: List<Stop>) : Paint

    /** Centre and radius in box fractions. */
    @Serializable
    @SerialName("radial")
    data class Radial(val cx: Double, val cy: Double, val r: Double, val stops: List<Stop>) : Paint
}

@Serializable
data class Stop(val at: Double, val color: ColorRef)

/** The [ColorRef] grammar, shared by the validator and the renderer's resolver. */
object ColorRefs {
    val TOKENS: List<String> = listOf("bg", "surface", "ink", "inkMuted", "accent", "accentInk", "slot")

    // Uppercase only: `#abcdef` and `#ABCDEF` would render alike but store, and hash, differently.
    private val hexRegex = Regex("^#[0-9A-F]{6}(?:[0-9A-F]{2})?$")
    private val opaqueHexRegex = Regex("^#[0-9A-F]{6}$")
    private val tokenRegex = Regex("^@(bg|surface|ink|inkMuted|accent|accentInk|slot)(?:/(0(?:\\.[0-9]{1,4})?|1(?:\\.0{1,4})?))?$")

    /** Any colour a layer may use: hex with optional alpha, or a palette token with optional alpha. */
    fun isValid(ref: String): Boolean = hexRegex.matches(ref) || tokenRegex.matches(ref)

    /** A literal colour the palette itself may hold: `#RRGGBB` or `#RRGGBBAA`, never a token. */
    fun isHex(value: String): Boolean = hexRegex.matches(value)

    /** An opaque `#RRGGBB`, the only form a brand colour takes. */
    fun isOpaqueHex(value: String): Boolean = opaqueHexRegex.matches(value)
}
